import React from 'react';

interface HosClockDisplayProps {
  currentDutyStatus: string | null;
  timeInCurrentStatus: string | null;
  breakTimeRemaining: string | null;
  driveTimeRemaining: string | null;
  shiftTimeRemaining: string | null;
  cycleTimeRemaining: string | null;
  samsaraDriverId: string | null;
  onParkingLocationClick?: () => void;
  parkingLocationName?: string | null;
}

// Max values in minutes for FMCSA 70-hour/8-day rule
const MAX_VALUES = {
  break: 8 * 60,    // 8 hours
  drive: 11 * 60,   // 11 hours
  shift: 14 * 60,   // 14 hours
  cycle: 70 * 60,   // 70 hours
};

// Parse time string like "5h 13m" or "5:13" to minutes
function parseTimeToMinutes(timeStr: string | null): number {
  if (!timeStr) return 0;

  // Handle "Xh Ym" format
  const hMatch = timeStr.match(/(\d+)h/);
  const mMatch = timeStr.match(/(\d+)m/);

  if (hMatch || mMatch) {
    const hours = hMatch ? parseInt(hMatch[1], 10) : 0;
    const minutes = mMatch ? parseInt(mMatch[1], 10) : 0;
    return hours * 60 + minutes;
  }

  // Handle "H:MM" or "HH:MM" format
  const colonMatch = timeStr.match(/^(\d+):(\d+)$/);
  if (colonMatch) {
    return parseInt(colonMatch[1], 10) * 60 + parseInt(colonMatch[2], 10);
  }

  return 0;
}

// Format minutes to "H:MM" display format
function formatTime(timeStr: string | null): string {
  if (!timeStr) return '--:--';

  const minutes = parseTimeToMinutes(timeStr);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, '0')}`;
}

// Get duty status abbreviation
function getDutyStatusAbbr(status: string | null): string {
  if (!status) return '--';
  const s = status.toLowerCase();
  if (s.includes('off') && s.includes('duty')) return 'OFF';
  if (s.includes('sleeper')) return 'SB';
  if (s.includes('driving')) return 'D';
  if (s.includes('on') && s.includes('duty')) return 'ON';
  if (s.includes('yard')) return 'YM';
  if (s.includes('personal')) return 'PC';
  // Return first 3 chars uppercase as fallback
  return status.substring(0, 3).toUpperCase();
}

// Get duty status background color
function getDutyStatusColor(status: string | null): string {
  if (!status) return 'bg-gray-400';
  const s = status.toLowerCase();
  if (s.includes('off') && s.includes('duty')) return 'bg-gray-500';
  if (s.includes('sleeper')) return 'bg-purple-500';
  if (s.includes('driving')) return 'bg-green-500';
  if (s.includes('on') && s.includes('duty')) return 'bg-blue-500';
  if (s.includes('yard')) return 'bg-yellow-500';
  if (s.includes('personal')) return 'bg-orange-500';
  return 'bg-gray-400';
}

interface CircularProgressProps {
  value: number;  // Current value in minutes
  max: number;    // Max value in minutes
  label: string;
  timeDisplay: string;
  size?: number;
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  max,
  label,
  timeDisplay,
  size = 48
}) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = Math.min(Math.max(value / max, 0), 1);
  const strokeDashoffset = circumference * (1 - percentage);

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg className="absolute" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-200 dark:text-gray-600"
          />
        </svg>
        {/* Progress circle */}
        <svg
          className="absolute transform -rotate-90"
          width={size}
          height={size}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="text-blue-500 transition-all duration-300"
          />
        </svg>
      </div>
      <span className="text-sm font-semibold text-heading mt-1">
        {timeDisplay}
      </span>
      <span className="text-xs text-muted">
        {label}
      </span>
    </div>
  );
};

export const HosClockDisplay: React.FC<HosClockDisplayProps> = ({
  currentDutyStatus,
  timeInCurrentStatus,
  breakTimeRemaining,
  driveTimeRemaining,
  shiftTimeRemaining,
  cycleTimeRemaining,
  samsaraDriverId,
  onParkingLocationClick,
  parkingLocationName,
}) => {
  const hasAnyHosData = breakTimeRemaining || driveTimeRemaining || shiftTimeRemaining || cycleTimeRemaining || currentDutyStatus;

  if (!hasAnyHosData && !parkingLocationName) {
    return null;
  }

  const breakMinutes = parseTimeToMinutes(breakTimeRemaining);
  const driveMinutes = parseTimeToMinutes(driveTimeRemaining);
  const shiftMinutes = parseTimeToMinutes(shiftTimeRemaining);
  const cycleMinutes = parseTimeToMinutes(cycleTimeRemaining);

  return (
    <div className="px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between gap-2">
        {/* Duty Status Badge */}
        {currentDutyStatus && (
          <div className="flex flex-col items-center">
            {samsaraDriverId ? (
              <a
                href={`https://cloud.samsara.com/o/39743/fleet/reports/hos/driver/${samsaraDriverId}`}
                target="_blank"
                rel="noopener noreferrer"
                title="View in Samsara"
                className={`${getDutyStatusColor(currentDutyStatus)} rounded-lg w-12 h-12 flex items-center justify-center hover:opacity-80 transition-opacity`}
              >
                <span className="text-white font-bold text-sm">
                  {getDutyStatusAbbr(currentDutyStatus)}
                </span>
              </a>
            ) : (
              <div
                className={`${getDutyStatusColor(currentDutyStatus)} rounded-lg w-12 h-12 flex items-center justify-center`}
              >
                <span className="text-white font-bold text-sm">
                  {getDutyStatusAbbr(currentDutyStatus)}
                </span>
              </div>
            )}
            <span className="text-sm font-semibold text-heading mt-1">
              {formatTime(timeInCurrentStatus)}
            </span>
            <span className="text-xs text-muted">
              {getDutyStatusAbbr(currentDutyStatus) === 'OFF' ? 'Off Duty' :
               getDutyStatusAbbr(currentDutyStatus) === 'SB' ? 'Sleeper' :
               getDutyStatusAbbr(currentDutyStatus) === 'D' ? 'Driving' :
               getDutyStatusAbbr(currentDutyStatus) === 'ON' ? 'On Duty' :
               getDutyStatusAbbr(currentDutyStatus) === 'YM' ? 'Yard' :
               getDutyStatusAbbr(currentDutyStatus) === 'PC' ? 'Personal' : 'Status'}
            </span>
          </div>
        )}

        {/* Circular Progress Indicators */}
        {breakTimeRemaining && (
          <CircularProgress
            value={breakMinutes}
            max={MAX_VALUES.break}
            label="Break"
            timeDisplay={formatTime(breakTimeRemaining)}
          />
        )}

        {driveTimeRemaining && (
          <CircularProgress
            value={driveMinutes}
            max={MAX_VALUES.drive}
            label="Drive"
            timeDisplay={formatTime(driveTimeRemaining)}
          />
        )}

        {shiftTimeRemaining && (
          <CircularProgress
            value={shiftMinutes}
            max={MAX_VALUES.shift}
            label="Shift"
            timeDisplay={formatTime(shiftTimeRemaining)}
          />
        )}

        {cycleTimeRemaining && (
          <CircularProgress
            value={cycleMinutes}
            max={MAX_VALUES.cycle}
            label="Cycle"
            timeDisplay={formatTime(cycleTimeRemaining)}
          />
        )}
      </div>

      {/* Parking Location - centered below HOS data */}
      {parkingLocationName && onParkingLocationClick && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-center">
          <button
            onClick={onParkingLocationClick}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
            title="View parking location on map"
          >
            {parkingLocationName}
          </button>
        </div>
      )}
    </div>
  );
};
